<template>
  <div v-if="!readonly">
    <el-form ref="formRef" :model="order" label-position="top" require-asterisk-position="right">  
      <!-- Progress bars -->
      <div v-if="automaticFit">
        <div class="row">
          <div v-loading="bodyAttributes.length <= 0 && authentication.isAuthenticated" class="col" element-loading-background="rgba(122, 122, 122, 0)">
            <el-form-item prop="scanFiles" :label="$t('views.customize.workflow.form.scanFiles')">
              <select id="scan" v-model="selectedBodyAttribute" class="form-select" @change="$emit('scanFileChanged', selectedBodyAttribute)">
                <option :value="null" :label="$t(`components.product-parameter.scan.none`)" />
                <option
                  v-for="bodyAttribute in bodyAttributes"
                  :key="bodyAttribute.characteristicId"
                  :value="bodyAttribute"
                  :label="$t(`components.product-parameter.scan.${bodyAttribute.bodyPart?.name}`) + ' - ' + bodyAttribute.scanFile?.name"
                />
              </select>
            </el-form-item>
          </div>
        </div>
      </div>
      <div>
        <div class="row mt-1">
          <div class="col">
            <el-form-item prop="heightWithoutDeduction">
              <div class="col">
                <label for="height-range" class="el-form-item__label mb-0"
                  >{{ $t('components.order-form.strength') }}                  
                </label>
                <el-slider id="height-range" v-model="order.heightWithoutDeduction" :min="2" :max="5" :step="0.5"/>
                <el-input-number v-model="order.heightWithoutDeduction" :step="0.5"/>
              </div>
            </el-form-item>
          </div>
        </div>      
      </div>
    </el-form>
  </div>
  <div v-else>
    <div v-if="!automaticFit">
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary">{{ $t('components.order-form.strength') }}</label>
        <label class="col-sm-6 form-label">{{ order?.heightWithoutDeduction }}</label>
      </div>
    </div>
    <div v-else>
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary">{{ $t('views.customize.workflow.form.scanFiles') }}</label>
        <label class="col-sm-6 form-label">{{ selectedBodyAttribute }}</label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Ref } from 'vue-facing-decorator';

import * as CustomerService from '@/services/api/customerService';
import Auth from '@/services/api/auth';
import { Body_Attribute } from '@/types/api/Patient/BodyAttribute';
import { Product } from '@/types/api/Model/Product';
import { ElForm } from 'element-plus';
import { OrderRequestData } from '@/services/api/orderService';
import ButtonCardConfig from '@/components/layout/ButtonCardConfig.vue';
import { useUserStore } from '@/stores/userStore';



@Component({
  components: {
    ButtonCardConfig
  },
  emits: ['scanFileChanged', 'zipChanged', 'infoButtonClicked', 'isValid'],
})

export default class ProductParameterCover extends Vue {
  @Prop() order!: OrderRequestData;
  @Prop() product!: Product;
  @Prop({ default: false }) readonly!: boolean;

  authentication = Auth.getInstance();
  userStore = useUserStore();


  @Ref('formRef') formRef!: InstanceType<typeof ElForm>;

  async validateForm(): Promise<boolean> {
    if (!this.formRef) {
      return false;
    }

    try {
      const valid = await this.formRef.validate();
      this.$emit('isValid', valid);
      return valid;
    } catch (error) {
      console.warn('Form validation error:', error);
      return false;
    }
  }
  bodyAttributes: Body_Attribute[] = [];

  selectedBodyAttribute: Body_Attribute | null = null;

  automaticFit = false;

  hideZipperCompanyNames: string[] = ['wjtortho'];
  showZipperOption = true;

  mounted(): void {
    this.order.heightWithoutDeduction = 3.5;
    this.order.depth = 0;
    this.order.calfCircumferenceWithoutDeduction = 0;

    if (this.authentication.user) {
      this.automaticFit = false;
      CustomerService.getBodyAttributesByUserId(this.authentication.user.userId).then((bodyAttributes) => {
        this.bodyAttributes = bodyAttributes;
      });
    }
  }

  beforeMount() {
    if (!this.order.colorList) {
      this.order.colorList = [''];
    }

    const queryParams = new URLSearchParams(window.location.search);
    const supplier = queryParams.get('supplier');
    if (supplier) {
      this.showZipperOption = !this.hideZipperCompanyNames.includes(supplier);
    }
  }

  automaticFitChanged(): void {
    if (!this.automaticFit) this.selectedBodyAttribute = null;
  }
}
</script>

<style scoped lang="scss">
.el-form-item {
  margin-top: 0.5rem;
  font-weight: normal;
  color: #1d1e19;
}

:deep(.el-form-item__label) {
  color: #1d1e19;
}

.text-secondary {
  color: #0000008d !important;
  text-align: left !important;
}

.row1 {
  border-bottom: 0.1rem solid #6767678d;
}

.form-label {
  font-size: 1rem;
  margin: 0.7rem 0 0.5rem 0;
  text-align: right;
}

:deep(.custom-switch .el-switch__label) {
  color: white !important;
}

.el-slider {
  margin: 0.2rem;
  width: auto;
}

.el-input-number {
  width: 100%;
}

.info-button:hover {
  cursor: pointer;
  color: #565656;
}

.side-button-group {
  display: flex;
  gap: 0.5rem; /* space between buttons */
  justify-content: flex-start;
  flex-wrap: wrap; /* wrap on smaller screens */
}

/* Target the img inside the card */
:deep(.card img.side-image) {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Optional: Adjust the card size from the parent */
:deep(.card) {
  width: 5.5rem !important;
  height: 9rem !important;
  padding: 0.0rem;
}
:deep(.card-title) {
  font-weight: 700;
  font-size: 12px;
}
</style>
